/**
 * THE WINDOW CLASSES (docs/platform-standards.md §18, KPR-1759).
 *
 * keepr lays out by the window, not the device. Three classes replace the one
 * cut at antd's `md` (768px) the client grew up with:
 *
 *   compact    under 600 wide     every phone held upright; Slide Over
 *   medium     600 to 1023        every iPad held upright; Split View ½ and ⅔
 *   expanded   1024 and up        iPads in landscape; laptops and desktops
 *
 * and one more fact, not a fourth class: a window under 500 tall is SHORT (a
 * phone held sideways, a squat Stage Manager window).
 *
 * This file is the one definition. `useWindowClass()` (src/hooks) answers from
 * these query strings through `matchMedia`, and every stylesheet rule that
 * switches on a class writes one of them out verbatim, so the hook and the CSS
 * can never disagree about where a class starts. `src/css/tokens.css` declares
 * the same strings as `@custom-media` (byte-equal; the window-class gate in
 * src/__tests__/windowClassSurfaces.test.js holds both).
 *
 * Why the rules spell the query out instead of writing `@media (--compact)`:
 * react-scripts 5 runs postcss-preset-env at stage 3 with `config: false`, and
 * custom media is a stage 2 feature, so the build leaves `(--compact)` alone and
 * no browser understands it yet. The rule would be dropped without a word.
 */

/** Where each class starts, in CSS pixels. */
export const WINDOW_EDGES = Object.freeze({
	medium: 600,
	expanded: 1024,
	// A window SHORTER than this keeps the compact chrome (§18).
	short: 500,
});

/**
 * The media queries, as the stylesheet writes them. The `.98` upper bounds are
 * the usual guard for fractional widths under zoom: 599.5 is compact, 600 is not.
 */
export const WINDOW_MEDIA = Object.freeze({
	compact: '(max-width: 599.98px)',
	medium: '(min-width: 600px) and (max-width: 1023.98px)',
	expanded: '(min-width: 1024px)',
	// medium and expanded together: "not compact", the old "from 768px up".
	'medium-up': '(min-width: 600px)',
	short: '(max-height: 499.98px)',
});

/** The three class names, in order. */
export const WINDOW_CLASSES = Object.freeze(['compact', 'medium', 'expanded']);

/**
 * The class of a window `width` × `height`, worked out from the numbers. The
 * hook reads `matchMedia` instead (so it agrees with the stylesheet to the
 * pixel); this is for code with a size in hand and for the tests that prove the
 * two say the same thing.
 */
export function windowClassFor(width, height = Infinity) {
	const w = Number(width);
	const windowClass = !(w >= WINDOW_EDGES.medium) ? 'compact' : w >= WINDOW_EDGES.expanded ? 'expanded' : 'medium';
	return { windowClass, short: Number(height) < WINDOW_EDGES.short };
}
